// v3.14.0 — Questions-for-Agency page.
//
// Step 5: wires `Generate` / `Regenerate` to the backend, renders the
// categorized question list with priority badges, source references, and
// rationale. Read-only render — edit mode + include/exclude + DOCX export
// arrive in Step 6.

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Building2,
  HelpCircle,
  Sparkles,
  RefreshCw,
  AlertCircle,
  Pencil,
  Save,
  X,
  Download,
  Copy,
  Plus,
  Trash2,
  Send,
  Lock,
  Unlock,
  History,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import PursuitNextStepFooter from '../../components/pursuit/PursuitNextStepFooter';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';
import PursuitPageShell from '../../components/pursuit/PursuitPageShell';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_STYLE = {
  high: 'bg-rose-50 text-rose-700 border-rose-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-slate-50 text-slate-700 border-slate-200',
};

function AgencyQuestionsPage() {
  const { pursuitId } = useParams();
  const { authToken } = useAuth();

  const [pursuit, setPursuit] = useState(null);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  }), [authToken]);

  const loadAll = useCallback(async () => {
    if (!pursuitId) return;
    setLoading(true);
    try {
      const [pRes, qRes] = await Promise.all([
        fetch(`${API}/api/pursuits/${pursuitId}`, { headers, credentials: 'include' }),
        fetch(`${API}/api/pursuits/${pursuitId}/agency-questions`, {
          headers, credentials: 'include',
        }),
      ]);
      if (pRes.ok) setPursuit(await pRes.json());
      if (qRes.ok) {
        const r = await qRes.json();
        setRecord(r.exists ? r : null);
      }
    } catch (e) {
      toast.error('Failed to load Questions for Agency');
    } finally {
      setLoading(false);
    }
  }, [pursuitId, headers]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(
        `${API}/api/pursuits/${pursuitId}/agency-questions/generate`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ mode: 'auto', max_questions: 15 }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(
          typeof err.detail === 'string'
            ? err.detail
            : err.detail?.message || 'Generation failed',
        );
        return;
      }
      const data = await res.json();
      setRecord(data);
      toast.success(`Drafted ${data.total_questions} question(s)`);
    } catch (e) {
      toast.error('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // PATCH save (used by edit mode + include-toggle in view mode)
  const handleSave = async (categories) => {
    try {
      const res = await fetch(
        `${API}/api/pursuits/${pursuitId}/agency-questions`,
        {
          method: 'PATCH',
          headers,
          credentials: 'include',
          body: JSON.stringify({ categories }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
        return false;
      }
      const data = await res.json();
      setRecord(prev => ({
        ...prev,
        categories: data.categories,
        total_questions: data.total_questions,
        edited_at: data.edited_at,
      }));
      return true;
    } catch (e) {
      toast.error('Save failed');
      return false;
    }
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(
        `${API}/api/pursuits/${pursuitId}/agency-questions/docx`,
        { headers, credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Download failed');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : `QuestionsForAgency_${pursuitId}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  // Copy the included questions to the clipboard as plain numbered text —
  // useful for the SAM.gov Q&A portal, CO email, or any text-only channel.
  const handleCopyAsText = async () => {
    if (!record) return;
    let n = 0;
    const lines = [];
    const oppName = pursuit?.opportunity_name || '';
    const sol = pursuit?.solicitation_number || record?.solicitation_snapshot?.solicitation_number || '';
    if (oppName) lines.push(`Questions for the Contracting Officer — ${oppName}`);
    if (sol) lines.push(`Solicitation: ${sol}`);
    lines.push('');
    for (const cat of record.categories || []) {
      const included = (cat.questions || []).filter(q => q.include !== false);
      if (included.length === 0) continue;
      lines.push(`== ${cat.name} ==`);
      for (const q of included) {
        n += 1;
        const ref = q.source_reference ? ` (Ref: ${q.source_reference})` : '';
        lines.push(`${n}. ${q.question}${ref}`);
        if (q.rationale) lines.push(`   Why: ${q.rationale}`);
      }
      lines.push('');
    }
    if (n === 0) {
      toast.error('No included questions to copy');
      return;
    }
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${n} question(s) to clipboard`);
    } catch (e) {
      toast.error('Could not copy to clipboard');
    }
  };

  // v3.14.2 — Submit to CO: stamps submitted_at/by + appends an entry to
  // submission_history[]. Locks the record from further PATCH/regenerate
  // (server enforces; the UI hides the edit/regen buttons too).
  const handleSubmit = async ({ submissionChannel, note }) => {
    try {
      const res = await fetch(
        `${API}/api/pursuits/${pursuitId}/agency-questions/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            submission_channel: submissionChannel || null,
            note: note || null,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(
          typeof err.detail === 'string'
            ? err.detail
            : err.detail?.message || 'Submission failed',
        );
        return false;
      }
      // Re-fetch the full record so the UI gets the new submission_history entry.
      const refresh = await fetch(
        `${API}/api/pursuits/${pursuitId}/agency-questions`,
        { headers, credentials: 'include' },
      );
      if (refresh.ok) {
        const r = await refresh.json();
        setRecord(r.exists ? r : null);
      }
      toast.success('Marked as submitted to the CO');
      return true;
    } catch (e) {
      toast.error('Submission failed');
      return false;
    }
  };

  const handleUnlock = async () => {
    try {
      const res = await fetch(
        `${API}/api/pursuits/${pursuitId}/agency-questions/unlock`,
        { method: 'POST', headers, credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Unlock failed');
        return false;
      }
      // Re-fetch to pick up cleared submitted_at + preserved history.
      const refresh = await fetch(
        `${API}/api/pursuits/${pursuitId}/agency-questions`,
        { headers, credentials: 'include' },
      );
      if (refresh.ok) {
        const r = await refresh.json();
        setRecord(r.exists ? r : null);
      }
      toast.success('Unlocked — you can now edit or regenerate');
      return true;
    } catch (e) {
      toast.error('Unlock failed');
      return false;
    }
  };

  if (loading) {
    return (
      <PursuitPageShell
        title="Questions for Agency"
        titleIcon={HelpCircle}
        showStepper={false}
        testId="aq-loading"
      >
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </PursuitPageShell>
    );
  }

  return (
    <PursuitPageShell
      eyebrow={<PageEyebrow pursuit={pursuit} />}
      title="Questions for Agency"
      titleIcon={HelpCircle}
      subtitle={pursuit?.opportunity_name || 'Clarifications to send to the contracting officer'}
      nextStepFor="agency_questions"
      testId="aq-shell"
    >
      {!record && (
        <EmptyState
          onGenerate={handleGenerate}
          generating={generating}
        />
      )}

      {record && (
        <QuestionsPreview
          record={record}
          pursuit={pursuit}
          onRegenerate={handleGenerate}
          generating={generating}
          onSave={handleSave}
          onDownload={handleDownload}
          onCopyAsText={handleCopyAsText}
          onSubmit={handleSubmit}
          onUnlock={handleUnlock}
        />
      )}
    </PursuitPageShell>
  );
}

// Eyebrow rendered inside PursuitPageShell — replaces the legacy PageHeader.
function PageEyebrow({ pursuit }) {
  const phaseLabel = pursuit?.procurement_phase
    ? pursuit.procurement_phase.replace(/_/g, ' ')
    : null;
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500">
      <Building2 className="w-3.5 h-3.5" />
      <span>{pursuit?.agency_name || 'Federal Agency'}</span>
      {phaseLabel && (
        <Badge variant="outline" className="ml-1 font-mono text-[10px]">{phaseLabel}</Badge>
      )}
    </div>
  );
}

function _OldPageHeader_unused({ pursuit }) {
  // Removed in v3.18.19 — replaced by PursuitPageShell + PageEyebrow.
  return null;
}

function EmptyState({ onGenerate, generating }) {
  return (
    <Card className="border-slate-200" data-testid="aq-empty">
      <CardContent className="p-10 text-center space-y-4">
        <div className="w-14 h-14 mx-auto bg-violet-100 rounded-full flex items-center justify-center">
          <Sparkles className="w-7 h-7 text-violet-700" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Generate clarification questions for the agency
          </h3>
          <p className="text-sm text-slate-600 mt-1 max-w-xl mx-auto">
            We&rsquo;ll review the solicitation and your knowledge base to draft
            a categorized list of questions worth sending to the contracting
            officer before you submit.
          </p>
        </div>
        <Button
          onClick={onGenerate}
          disabled={generating}
          className="bg-violet-700 hover:bg-violet-800"
          data-testid="aq-generate-btn"
        >
          {generating ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Drafting questions…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Questions
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

function QuestionsPreview({
  record,
  pursuit,
  onRegenerate,
  generating,
  onSave,
  onDownload,
  onCopyAsText,
  onSubmit,
  onUnlock,
}) {
  const generatedAt = record.generated_at
    ? new Date(record.generated_at).toLocaleString()
    : '';
  const editedAt = record.edited_at
    ? new Date(record.edited_at).toLocaleString()
    : '';

  // v3.14.2 — locked = list has been submitted to the CO.
  const submittedAt = record.submitted_at
    ? new Date(record.submitted_at).toLocaleString()
    : null;
  const locked = !!record.submitted_at;
  const submissionHistory = record.submission_history || [];

  const [editing, setEditing] = useState(false);
  const [draftCategories, setDraftCategories] = useState(record.categories || []);
  const [saving, setSaving] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Keep the local draft in sync whenever the server-side record changes.
  // We deep-clone via JSON so toggling include in view mode (which writes
  // through draftCategories) doesn't mutate the prop.
  useEffect(() => {
    setDraftCategories(JSON.parse(JSON.stringify(record.categories || [])));
  }, [record.categories]);

  const startEdit = () => {
    setDraftCategories(JSON.parse(JSON.stringify(record.categories || [])));
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraftCategories(JSON.parse(JSON.stringify(record.categories || [])));
    setEditing(false);
  };
  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave(draftCategories);
    setSaving(false);
    if (ok) {
      setEditing(false);
      toast.success('Edits saved');
    }
  };

  // Toggling include in VIEW mode persists immediately so users can cull the
  // list without entering edit mode. In edit mode the toggle just updates the
  // local draft (saved with everything else on Save Changes).
  const onToggleInclude = async (qid, value) => {
    const next = draftCategories.map(cat => ({
      ...cat,
      questions: (cat.questions || []).map(q =>
        q.id === qid ? { ...q, include: value } : q,
      ),
    }));
    setDraftCategories(next);
    if (!editing) {
      const ok = await onSave(next);
      if (!ok) {
        // Revert on failure.
        setDraftCategories(JSON.parse(JSON.stringify(record.categories || [])));
      }
    }
  };

  const updateQuestion = (catIdx, qIdx, patch) => {
    setDraftCategories(prev => prev.map((c, ci) => {
      if (ci !== catIdx) return c;
      return {
        ...c,
        questions: c.questions.map((q, qi) => qi === qIdx ? { ...q, ...patch } : q),
      };
    }));
  };

  const addQuestion = (catIdx) => {
    setDraftCategories(prev => prev.map((c, ci) => {
      if (ci !== catIdx) return c;
      return {
        ...c,
        questions: [
          ...(c.questions || []),
          {
            // Empty id triggers a fresh UUID server-side on save.
            id: '',
            question: '',
            source_reference: '',
            rationale: '',
            priority: 'medium',
            include: true,
          },
        ],
      };
    }));
  };

  const removeQuestion = (catIdx, qIdx) => {
    setDraftCategories(prev => prev.map((c, ci) => {
      if (ci !== catIdx) return c;
      return {
        ...c,
        questions: c.questions.filter((_, qi) => qi !== qIdx),
      };
    }));
  };

  // Numbering: only included questions get a sequential number.
  const numbered = useMemo(() => {
    let n = 0;
    return draftCategories.map(cat => ({
      ...cat,
      questions: (cat.questions || []).map(q => {
        if (q.include !== false) {
          n += 1;
          return { ...q, _displayNumber: n };
        }
        return { ...q, _displayNumber: null };
      }),
    }));
  }, [draftCategories]);

  const totalIncluded = useMemo(() => {
    return draftCategories.reduce((sum, c) => sum + (c.questions || []).filter(q => q.include !== false).length, 0);
  }, [draftCategories]);
  const totalAll = useMemo(() => {
    return draftCategories.reduce((sum, c) => sum + (c.questions || []).length, 0);
  }, [draftCategories]);

  return (
    <Card className="border-slate-200" data-testid="aq-preview">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3 flex-wrap">
        <div>
          <CardTitle className="text-lg flex items-center gap-2 flex-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {totalIncluded} of {totalAll} question{totalAll === 1 ? '' : 's'} included
            {locked && (
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"
                data-testid="aq-submitted-badge"
              >
                <Lock className="w-3 h-3 mr-1" />
                Submitted
              </Badge>
            )}
          </CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">
            Generated {generatedAt}
            {editedAt && (
              <span className="ml-2 text-slate-400">· edited {editedAt}</span>
            )}
            {submittedAt && (
              <span className="ml-2 text-emerald-700">· submitted {submittedAt}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {locked ? (
            // Locked-state action set: only Download, Copy, and Unlock are
            // available. Edit/Regenerate/Submit are all gated by unlock.
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onCopyAsText}
                data-testid="aq-copy-btn"
              >
                <Copy className="w-4 h-4 mr-1.5" />
                Copy as text
              </Button>
              <Button
                size="sm"
                onClick={onDownload}
                disabled={totalIncluded === 0}
                className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300"
                data-testid="aq-download-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Download DOCX
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onUnlock}
                className="text-amber-700 border-amber-200 hover:bg-amber-50"
                data-testid="aq-unlock-btn"
              >
                <Unlock className="w-4 h-4 mr-1.5" />
                Unlock
              </Button>
            </>
          ) : !editing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={startEdit}
                data-testid="aq-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-1.5" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onRegenerate}
                disabled={generating}
                data-testid="aq-regenerate-btn"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                    Regenerating…
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Regenerate
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onCopyAsText}
                data-testid="aq-copy-btn"
              >
                <Copy className="w-4 h-4 mr-1.5" />
                Copy as text
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDownload}
                disabled={totalIncluded === 0}
                data-testid="aq-download-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Download DOCX
              </Button>
              <Button
                size="sm"
                onClick={() => setSubmitOpen(true)}
                disabled={totalIncluded === 0}
                className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300"
                data-testid="aq-submit-btn"
              >
                <Send className="w-4 h-4 mr-1.5" />
                Mark as Submitted
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={cancelEdit}
                disabled={saving}
                data-testid="aq-cancel-btn"
              >
                <X className="w-4 h-4 mr-1.5" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-violet-700 hover:bg-violet-800"
                data-testid="aq-save-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1.5" />
                    Save Changes
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {totalAll === 0 && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              The AI didn&rsquo;t find anything worth flagging. This usually means the
              solicitation hasn&rsquo;t been ingested yet, or its scope is too thin
              for meaningful clarifications. Try ingesting the RFP first via{' '}
              <strong>Document Ingestion</strong>, then regenerate.
            </div>
          </div>
        )}

        {numbered.map((cat, ci) => (
          <CategoryBlock
            key={`${cat.name}-${ci}`}
            category={cat}
            catIndex={ci}
            editing={editing}
            locked={locked}
            onToggleInclude={onToggleInclude}
            onUpdateQuestion={updateQuestion}
            onAddQuestion={addQuestion}
            onRemoveQuestion={removeQuestion}
          />
        ))}

        {/* v3.14.2 — Submission history accordion (visible whenever the
            list has been submitted at least once, even if currently unlocked) */}
        {submissionHistory.length > 0 && (
          <SubmissionHistory
            history={submissionHistory}
            open={historyOpen}
            onToggle={() => setHistoryOpen(o => !o)}
          />
        )}
      </CardContent>
      {submitOpen && (
        <SubmitDialog
          open={submitOpen}
          onClose={() => setSubmitOpen(false)}
          includedCount={totalIncluded}
          submitting={submitting}
          onConfirm={async ({ channel, note }) => {
            setSubmitting(true);
            const ok = await onSubmit({ submissionChannel: channel, note });
            setSubmitting(false);
            if (ok) setSubmitOpen(false);
          }}
        />
      )}
    </Card>
  );
}

function CategoryBlock({
  category,
  catIndex,
  editing,
  locked,
  onToggleInclude,
  onUpdateQuestion,
  onAddQuestion,
  onRemoveQuestion,
}) {
  const questions = category.questions || [];
  if (!editing && questions.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 border-b border-slate-100 pb-1.5">
        <h3 className="text-sm font-semibold text-slate-900">{category.name}</h3>
        <Badge variant="outline" className="text-[10px]">{questions.length}</Badge>
      </div>
      <div className="space-y-3">
        {questions.map((q, qi) => (
          <QuestionRow
            key={q.id || `new-${qi}`}
            q={q}
            editing={editing}
            locked={locked}
            onToggleInclude={onToggleInclude}
            onUpdate={(patch) => onUpdateQuestion(catIndex, qi, patch)}
            onRemove={() => onRemoveQuestion(catIndex, qi)}
          />
        ))}
        {editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddQuestion(catIndex)}
            className="text-violet-700 border-violet-200 hover:bg-violet-50"
            data-testid={`aq-add-question-${catIndex}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add question
          </Button>
        )}
      </div>
    </div>
  );
}

function QuestionRow({ q, editing, locked, onToggleInclude, onUpdate, onRemove }) {
  const priorityClass = PRIORITY_STYLE[q.priority] || PRIORITY_STYLE.medium;
  const numberLabel = q._displayNumber != null ? `${q._displayNumber}.` : '—';
  const isExcluded = q.include === false;

  return (
    <div
      className={`border-l-2 pl-3 py-1 ${isExcluded ? 'border-slate-200 opacity-60' : 'border-violet-300'}`}
      data-testid={`aq-question-${q.id || 'new'}`}
    >
      {editing ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Checkbox
              checked={!isExcluded}
              onCheckedChange={(v) => onToggleInclude(q.id, !!v)}
              data-testid={`aq-include-toggle-${q.id || 'new'}`}
            />
            <span className="font-mono text-xs text-slate-500">{numberLabel}</span>
            <Select
              value={q.priority || 'medium'}
              onValueChange={(v) => onUpdate({ priority: v })}
            >
              <SelectTrigger className="h-7 w-28 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={q.source_reference || ''}
              placeholder="RFP Section / page"
              onChange={(e) => onUpdate({ source_reference: e.target.value })}
              className="h-7 text-xs flex-1 min-w-[180px]"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              className="text-rose-600 hover:bg-rose-50 px-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          <Textarea
            value={q.question || ''}
            placeholder="Question to ask the contracting officer…"
            rows={2}
            onChange={(e) => onUpdate({ question: e.target.value })}
            className="text-sm"
            data-testid={`aq-edit-question-text-${q.id || 'new'}`}
          />
          <Textarea
            value={q.rationale || ''}
            placeholder="Rationale (why we're asking)"
            rows={2}
            onChange={(e) => onUpdate({ rationale: e.target.value })}
            className="text-xs"
          />
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 flex-wrap">
            <Checkbox
              checked={!isExcluded}
              onCheckedChange={(v) => onToggleInclude(q.id, !!v)}
              disabled={locked}
              className="translate-y-0.5"
              data-testid={`aq-include-toggle-${q.id || 'new'}`}
            />
            <span className="font-mono text-xs text-slate-500 flex-shrink-0">{numberLabel}</span>
            <Badge
              variant="outline"
              className={`text-[10px] uppercase font-medium ${priorityClass}`}
            >
              {q.priority || 'medium'}
            </Badge>
            {q.source_reference && (
              <span className="text-[11px] text-slate-500 italic">{q.source_reference}</span>
            )}
            {isExcluded && (
              <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">
                Excluded
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-800 mt-1 leading-relaxed">{q.question}</p>
          {q.rationale && (
            <p className="text-xs text-slate-500 mt-1">
              <span className="font-semibold text-slate-600">Why:</span> {q.rationale}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Submit dialog — collects optional channel + note, requires explicit confirm.
// ---------------------------------------------------------------------------

function SubmitDialog({ open, onClose, includedCount, submitting, onConfirm }) {
  const [channel, setChannel] = useState('');
  const [note, setNote] = useState('');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="aq-submit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-700" />
            Mark questions as submitted
          </DialogTitle>
          <DialogDescription>
            Lock this list so it can&rsquo;t be edited or regenerated. You can
            always unlock later if the agency asks for a revision &mdash;
            the submission history is preserved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            <strong>{includedCount}</strong> included question{includedCount === 1 ? '' : 's'} will be
            snapshotted to the audit trail.
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700">Submission channel (optional)</Label>
            <Input
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              placeholder="e.g. SAM.gov Q&A portal, Email to CO"
              className="mt-1 text-sm"
              data-testid="aq-submit-channel"
              disabled={submitting}
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700">Note for the audit trail (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Sent 5 business days before due date"
              rows={2}
              className="mt-1 text-sm"
              data-testid="aq-submit-note"
              disabled={submitting}
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={() => onConfirm({ channel, note })}
            disabled={submitting}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="aq-submit-confirm"
          >
            {submitting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-1.5" />
                Confirm Submission
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Submission history accordion
// ---------------------------------------------------------------------------

function SubmissionHistory({ history, open, onToggle }) {
  return (
    <div className="border border-slate-200 rounded-md" data-testid="aq-history">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-slate-50 text-left"
        data-testid="aq-history-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">
            Submission history
          </span>
          <Badge variant="outline" className="text-[10px]">{history.length}</Badge>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {open && (
        <div className="border-t border-slate-200 p-3 space-y-3 bg-slate-50/50">
          {history.map((entry, i) => (
            <div key={`hist-${i}`} className="text-xs">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-mono text-slate-500">#{history.length - i}</span>
                <span className="font-medium text-slate-800">
                  {entry.submitted_by || '—'}
                </span>
                <span className="text-slate-500">
                  · {entry.submitted_at ? new Date(entry.submitted_at).toLocaleString() : ''}
                </span>
                {entry.submission_channel && (
                  <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                    {entry.submission_channel}
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px]">
                  {entry.included_count || 0} q{(entry.included_count === 1) ? '' : 's'}
                </Badge>
              </div>
              {entry.note && (
                <p className="text-slate-600 mt-1 italic">&ldquo;{entry.note}&rdquo;</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default AgencyQuestionsPage;
